import { posthog } from './posthogClient';

type StandardPayload = {
  user_id?: string;
  client_id?: string;
  process?: string;
  step?: string;
  timestamp?: string; // ISO
  duration_sec?: number;
  area?: string;
};

const nowISO = () => new Date().toISOString();

const cap = (name: string, props?: Record<string, unknown>) => {
  if (!posthog.__loaded) return;

  try {
    posthog.capture(name, props);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[PostHog] Failed to capture event:', name);
  }
};

export const trackUserLoggedIn = (data: { user_id: string; process: string; step: string; email?: string; name?: string }) =>
  cap('user_logged_in', {
    user_id: String(data.user_id),
    client_id: posthog.get_distinct_id?.(),
    process: data.process || 'auth',
    step: data.step || 'login_ok',
    email: data.email,
    name: data.name,
    timestamp: nowISO(),
  });

/** 2) page view */
export const trackViewedPage = (p: StandardPayload & { path: string; title?: string }) =>
  cap('viewed_page', { ...p, timestamp: p.timestamp || nowISO() });

/** 3) task started */
export const trackStartedTask = (p: StandardPayload & { task: string }) => {
  cap('started_task', { ...p, timestamp: p.timestamp || nowISO() });
};

/** 4) task completed */
export const trackCompletedTask = (p: StandardPayload & { task: string; success?: boolean }) =>
  cap('completed_task', { ...p, timestamp: p.timestamp || nowISO() });

/** 5) form submit success */
export const trackFormSubmitSuccess = (p: StandardPayload & { form: string }) =>
  cap('form_submit_success', { ...p, timestamp: p.timestamp || nowISO() });

/** 6) form submit error */
export const trackFormSubmitError = (p: StandardPayload & { form: string; error?: string }) =>
  cap('form_submit_error', { ...p, timestamp: p.timestamp || nowISO() });

/** 6.1) form submitted (generic, outcome-agnostic) */
export const trackFormSubmitted = (p: StandardPayload & { form: string }) => cap('form_submitted', { ...p, timestamp: p.timestamp || nowISO() });

/** 7) button clicked */
export const trackClickedButton = (p: StandardPayload & { button: string }) => cap('clicked_button', { ...p, timestamp: p.timestamp || nowISO() });

/** 8) feature used */
export const trackFeatureUsed = (p: StandardPayload & { feature: string }) => cap('feature_used', { ...p, timestamp: p.timestamp || nowISO() });

/** 9) session duration */
export const trackSessionEnd = (p: StandardPayload & { reason?: 'unload' | 'manual' }) =>
  cap('session_duration', { ...p, timestamp: p.timestamp || nowISO() });

// ========== App-specific UX events for user forms/listings ==========

// quick search (debounced upstream)
export const trackQuickSearch = (
  p: StandardPayload & { area: 'listing' | 'detail_subsection' | 'detail' | string; agendaId?: string; viewId?: string; query?: string }
) => {
  const query = (p.query || '').toString();
  cap('quick_search', { ...p, query, query_len: query.length, timestamp: p.timestamp || nowISO() });
};

// filter drawer open/apply
export const trackFilterOpened = (p: StandardPayload & { agendaId?: string; viewId?: string }) =>
  cap('filter_opened', { ...p, timestamp: p.timestamp || nowISO() });

export const trackFilterApplied = (p: StandardPayload & { agendaId?: string; viewId?: string; count?: number }) =>
  cap('filter_applied', { ...p, timestamp: p.timestamp || nowISO() });

// row click to open record detail
export const trackOpenRecordDetail = (
  p: StandardPayload & { agendaId?: string; viewId?: string; recordId?: string; from?: 'listing' | 'subsection' | string }
) => cap('open_record_detail', { ...p, timestamp: p.timestamp || nowISO() });

// form buttons (custom actions)
export const trackFormButtonClicked = (p: StandardPayload & { button: string; context?: 'detail' | 'edit' | string }) =>
  cap('form_button_clicked', { ...p, timestamp: p.timestamp || nowISO() });

// navigate between records (detail view toolbar)
export const trackNavigateBetweenRecords = (
  p: StandardPayload & { agendaId?: string; fromRecordId?: string; toRecordId?: string; direction: 'prev' | 'next'; area?: string }
) => cap('navigate_between_records', { ...p, timestamp: p.timestamp || nowISO() });

// open record versions (versioning modal)
export const trackOpenRecordVersions = (p: StandardPayload & { agendaId?: string; recordId?: string }) =>
  cap('open_record_versions', { ...p, timestamp: p.timestamp || nowISO() });

// ------- Task correlation for add/edit flows --------
type PendingTask = {
  id: string;
  task: 'add_record' | 'edit_record';
  agendaId?: string;
  recordId?: string;
  started_at: string;
};

const PENDING_KEY = 'ph_pending_task';

const setPendingTask = (task: PendingTask | null) => {
  try {
    if (!task) {
      localStorage.removeItem(PENDING_KEY);
      return;
    }
    localStorage.setItem(PENDING_KEY, JSON.stringify(task));
  } catch {
    // ignore
  }
};

const getPendingTask = (): PendingTask | null => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PendingTask;
  } catch {
    return null;
  }
};

export const startAddRecord = (p: StandardPayload & { agendaId?: string }) => {
  const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  setPendingTask({ id, task: 'add_record', agendaId: p.agendaId, started_at: nowISO() });
  trackStartedTask({ task: 'add_record', process: p.process || 'record', step: p.step || 'start' });
};

export const startEditRecord = (p: StandardPayload & { agendaId?: string; recordId?: string }) => {
  const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  setPendingTask({ id, task: 'edit_record', agendaId: p.agendaId, recordId: p.recordId, started_at: nowISO() });
  trackStartedTask({ task: 'edit_record', process: p.process || 'record', step: p.step || 'start' });
};

export const endRecordAction = (p: StandardPayload & { action: 'save' | 'save_and_close' | 'cancel'; success?: boolean }) => {
  const pending = getPendingTask();
  trackCompletedTask({ task: pending?.task || 'edit_record', process: p.process || 'record', step: p.action, success: p.success });
  setPendingTask(null);
};

export const safeTrack = <T extends Record<string, unknown>>(trackFn: (payload: T) => void, payload: T): void => {
  try {
    trackFn(payload);
  } catch {
    // ignore
  }
};
